import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { observer } from 'mobx-react';
import { IAdminxStore } from '../../../../modules/stores';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { Disable } from '@monorepo/base/src/components/disable/disable';
import { Toggle } from '@monorepo/base/src/components/form/toggle/toggle';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { Spacer, SpacerX } from '@monorepo/base/src/components/spacer/spacer';
import { sentenceCase } from 'change-case';
import styles from './duplicate-bidder.module.scss';
import { ErrorMsg } from '../../../../modules/components/error-msg/error-msg';
import { BidderCampaignForm } from '../../../../modules/components/forms/bidder-campaign/bidder-campaign.form';
import { Statuses } from '../../../../modules/enums/status';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { Fragment, useEffect, useState } from 'react';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';
import { useBidderCampaignGet } from '../../../../modules/hooks/apis/use-bidder-campaign-get';
import { useParams } from 'react-router-dom';
import { BidderApi } from '../../../../modules/apis/bidder.api';
import routes from '../../../../modules/routes/index.routes';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import { useBidderCampaignTableOfContents } from '../../../../modules/hooks/toc/bidder-campaign.toc';
import { Nav, NavGroup, NavItem, NavPage } from '@monorepo/base/src/components/nav/nav';
import { TableOfContents } from '@monorepo/base/src/components/table-of-contents/table-of-contents';
import { ActionModal, ModalTypes } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { Card } from '@monorepo/base/src/components/card/card';
import { Flex } from '@monorepo/base/src/components/flex/flex';
import { GButton } from '@monorepo/base/src/components/buttons/group/button/g-button';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { ButtonGroup } from '@monorepo/base/src/components/buttons/group/button-group';
import { BidderDeepCloneCampaignDomainStatus } from '../../../../modules/models/bidder-campaign/bidder-camapign.model';
import { BarLoader } from '@monorepo/base/src/components/bar-loader/bar-loader';

export const DuplicateBidderCampaign = observer(() => {
	const { bidderStore, modalsStore } = useStores<IAdminxStore>();
	const bidderCampaignCrudStore = bidderStore.getCrud();
	const isLoading = bidderCampaignCrudStore.getIsLoading();
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const error = bidderCampaignCrudStore.getHttpError();
	const campaign = bidderCampaignCrudStore.getData().campaign;
	const originalCampaign = bidderCampaignCrudStore.getOriginData().campaign;
	const navigate = useNavigateParams();
	const { dispatchLog } = useLogs();
	const { bidderCampaignId } = useParams();

	const { currentRoute } = useRoute();
	const [activeRoute, setActiveRoute] = useState<string>(currentRoute);
	const { tableOfContents, isTableOfContents, setIsTableOfContents } = useBidderCampaignTableOfContents();

	const bidderCampaignGetResult = useBidderCampaignGet({ id: bidderCampaignId });

	useEffect(() => {
		setIsTableOfContents(true);

		return () => {
			bidderCampaignCrudStore.reset();
		};
	}, []);

	useEffect(() => {
		if (
			bidderCampaignGetResult?.loaded &&
			!bidderCampaignGetResult?.error &&
			JSON.stringify(originalCampaign?.campaign_publisher_domain_status_limits) ===
				JSON.stringify(campaign.campaign_publisher_domain_status_limits) &&
			!campaign.deep_clone_campaign.campaign_publisher_lists
		) {
			campaign.campaign_publisher_domain_status_limits = [];
			campaign.campaign_publisher_lists = { white_list: [], black_list: [] };
			campaign.is_duplicate = true;
			campaign.campaign_status = Statuses.Paused;
			campaign.deep_clone_campaign.source_campaign_id = Number(bidderCampaignId);
			campaign.campaign_targeting?.forEach(targeting => {
				targeting.campaign_targeting_links = [];
			});
		}
	}, [bidderCampaignGetResult?.loaded]);

	const onDuplicateCampaign = () => {
		if (!bidderCampaignCrudStore.isValid()) {
			bidderCampaignCrudStore
				.getFormStore()
				.getErrors()
				.forEach(error => {
					dispatchLog({
						msg: error.getMsg(),
						type: DispatchLogsTypes.Error,
					});
				});

			return;
		}

		setIsSaving(true);

		BidderApi.cloneCampaign({
			campaign: bidderCampaignCrudStore.getCreateFormData().toObject(),
			deep_clone: campaign.deep_clone_campaign,
		})
			.then(res => {
				navigate(routes.bidder.campaigns());

				const msg = res.clone_info?.length
					? `duplicated with the following issues: <br />${res.clone_info.join('<br />')}`
					: 'successfully duplicated';

				dispatchLog({
					msg: `Campaign ${campaign?.campaign_name} was ${msg}`,
					fullHeight: true,
					type: DispatchLogsTypes.Success,
				});
			})
			.catch((e: { data: { success: boolean; errors: { message: string; field: string }[] } }) => {
				modalsStore.addModal({
					children: (
						<ActionModal type={ModalTypes.Error} primaryBtnTitle={'OK'}>
							<div className={styles.errorWrapper}>
								<h2 className={styles.title}>{`Failed to duplicate campaign.`}</h2>
								{e.data.errors.map(error => (
									<div className={styles.errorContent}>{`${error.field}: ${error.message}`}</div>
								))}
							</div>
						</ActionModal>
					),
				});
			})
			.finally(() => {
				setIsSaving(false);
			});
	};

	return (
		<NavPage>
			<Nav onNavClick={({ to }) => to && setActiveRoute(to)} activeRoute={activeRoute}>
				<NavItem to={`${routes.bidder.campaigns()}`}>Campaigns</NavItem>
				<Fragment>
					{isTableOfContents && (
						<NavGroup title={'Table of contents'}>
							<TableOfContents heads={tableOfContents} />
						</NavGroup>
					)}
				</Fragment>
			</Nav>
			<Page unstyled={true}>
				<Disable is={isLoading || isSaving}>
					<Page>
						<div style={{ margin: '-24px 0 20px 0' }}>
							<BarLoader is={isLoading} />
						</div>
						<Pagebar>
							<PagebarTitle unstyled={true}>
								<div className={styles.title}>Duplicate Campaign</div>
								<SpacerX />
								<Toggle
									label={
										campaign.campaign_status === Statuses.Active
											? sentenceCase(Statuses.Active)
											: sentenceCase(Statuses.Paused)
									}
									onChange={status => {
										campaign.campaign_status = status ? Statuses.Active : Statuses.Paused;
									}}
									defaultValue={campaign.campaign_status === Statuses.Active}
									disabled={true}
								/>
							</PagebarTitle>
						</Pagebar>

						<Fragment>
							<Card title="Deep Clone Options">
								<Flex className={styles.cloneOptions}>
									<Card>
										<Toggle
											label="Publisher lists and limits"
											onChange={campaign_publisher_lists => {
												if (campaign_publisher_lists) {
													campaign.campaign_publisher_domain_status_limits =
														originalCampaign.campaign_publisher_domain_status_limits;
													campaign.campaign_publisher_lists = originalCampaign.campaign_publisher_lists;
												} else {
													campaign.campaign_publisher_domain_status_limits = [];
													campaign.campaign_publisher_lists = { white_list: [], black_list: [] };
												}

												campaign.deep_clone_campaign = {
													...campaign.deep_clone_campaign,
													campaign_publisher_lists: Number(campaign_publisher_lists),
												};
											}}
											defaultValue={!!campaign.deep_clone_campaign.campaign_publisher_lists}
										/>
										<Spacer />
										<Toggle
											label="Black/white list domains"
											onChange={campaign_domains => {
												campaign.deep_clone_campaign = {
													...campaign.deep_clone_campaign,
													campaign_domains: Number(campaign_domains),
												};
											}}
											defaultValue={!!campaign.deep_clone_campaign.campaign_domains}
										/>
										<Spacer />
										<Toggle
											label="Black/white list isps"
											onChange={campaign_isps => {
												campaign.deep_clone_campaign = {
													...campaign.deep_clone_campaign,
													campaign_isps: Number(campaign_isps),
												};
											}}
											defaultValue={!!campaign.deep_clone_campaign.campaign_isps}
										/>
										<Spacer />
										<Toggle
											label="Black/white list subids"
											onChange={campaign_subids => {
												campaign.deep_clone_campaign = {
													...campaign.deep_clone_campaign,
													campaign_subids: Number(campaign_subids),
												};
											}}
											defaultValue={!!campaign.deep_clone_campaign.campaign_subids}
										/>
										<Spacer />
										<Toggle
											label="Preferences"
											onChange={campaign_preferences => {
												campaign.deep_clone_campaign = {
													...campaign.deep_clone_campaign,
													campaign_preferences: Number(campaign_preferences),
												};
											}}
											defaultValue={!!campaign.deep_clone_campaign.campaign_preferences}
										/>
										<Spacer />
										<Toggle
											label="Dynamic filters"
											onChange={campaign_dynamic_filters => {
												campaign.deep_clone_campaign = {
													...campaign.deep_clone_campaign,
													campaign_dynamic_filters: Number(campaign_dynamic_filters),
												};
											}}
											defaultValue={!!campaign.deep_clone_campaign.campaign_dynamic_filters}
										/>
									</Card>
									<Card>
										<ButtonGroup
											className={styles.buttonGroup}
											defaultValue={BidderDeepCloneCampaignDomainStatus.None}
											title={'Domain statuses'}
											onChange={(setValues: Set<string>) => {
												const val = setValues.values().next().value;
												const statusesVal = Number(val === BidderDeepCloneCampaignDomainStatus.All);

												campaign.deep_clone_campaign = {
													...campaign.deep_clone_campaign,
													campaign_domain_statuses_select: val,
													campaign_domain_statuses_medium_good: statusesVal,
													campaign_domain_statuses_trash: statusesVal,
													campaign_domain_statuses_new_rt2_sale: statusesVal,
												};
											}}>
											<GButton
												text={'None'}
												value={BidderDeepCloneCampaignDomainStatus.None}
												debugProps={{ dataAttrs: [new DataAttribute('id', 'distribution_eager')] }}
											/>
											<GButton
												text={'All'}
												value={BidderDeepCloneCampaignDomainStatus.All}
												debugProps={{ dataAttrs: [new DataAttribute('id', 'distribution_eager')] }}
											/>
											<GButton
												text={'Select'}
												value={BidderDeepCloneCampaignDomainStatus.Select}
												debugProps={{ dataAttrs: [new DataAttribute('id', 'distribution_eager')] }}
											/>
										</ButtonGroup>

										{campaign.deep_clone_campaign.campaign_domain_statuses_select ===
											BidderDeepCloneCampaignDomainStatus.Select && (
											<Fragment>
												<Spacer />
												<Spacer />
												<Toggle
													label="Medium / Good"
													defaultValue={!!campaign.deep_clone_campaign.campaign_domain_statuses_medium_good}
													onChange={() => {
														campaign.deep_clone_campaign = {
															...campaign.deep_clone_campaign,
															campaign_domain_statuses_medium_good: Number(
																!campaign.deep_clone_campaign.campaign_domain_statuses_medium_good
															),
														};
													}}
												/>
												<Spacer />
												<Toggle
													label="Trash"
													defaultValue={!!campaign.deep_clone_campaign.campaign_domain_statuses_trash}
													onChange={() => {
														campaign.deep_clone_campaign = {
															...campaign.deep_clone_campaign,
															campaign_domain_statuses_trash: Number(
																!campaign.deep_clone_campaign.campaign_domain_statuses_trash
															),
														};
													}}
												/>
												<Spacer />
												<Toggle
													label="New & RT2 with sale"
													defaultValue={!!campaign.deep_clone_campaign.campaign_domain_statuses_new_rt2_sale}
													onChange={() => {
														campaign.deep_clone_campaign = {
															...campaign.deep_clone_campaign,
															campaign_domain_statuses_new_rt2_sale: Number(
																!campaign.deep_clone_campaign.campaign_domain_statuses_new_rt2_sale
															),
														};
													}}
												/>
											</Fragment>
										)}
									</Card>
								</Flex>
							</Card>

							<Spacer />
						</Fragment>

						<BidderCampaignForm />
					</Page>
					<BottomBar>
						{error ? <ErrorMsg error={error} /> : null}
						<BottomBarActions>
							<PrimaryButton
								onClick={onDuplicateCampaign}
								isRotateIcon={isLoading || isSaving}
								icon={isLoading || isSaving ? 'loading-01' : 'send-03'}>
								Save
							</PrimaryButton>
						</BottomBarActions>
					</BottomBar>
				</Disable>
			</Page>
		</NavPage>
	);
});
